
import {
  Component, Ref, Watch, Mixins, Inject,
} from 'vue-property-decorator';
import { debounce } from 'ts-debounce';
import { InputValidationRules } from 'vuetify';
import { VForm } from '@/features/common/types';
import {
  CurrencyCodeType,
  GeneralInfo, InProgress, ShipFrom, ShipTo,
} from '@/features/shipment/types';
import {
  primaryCurrency,
  reasonsForExport,
  ReasonsForExportOptionsType,
} from '@/features/common/raw';
import { UserPreferences } from '@/features/settings/types';
import { requiredRules } from '@/features/common/helpers/validationRules';
import {
  CommercialInvoiceValidationState,
  rulesIfAttempted as rulesIfAttemptedFn,
} from '@/features/shipment/helpers/commercialInvoiceValidation';
import { getCommercialInvoiceGeneralDescriptions } from '@/features/shipment-history/services/shipment-history.services';
import ResponsivenessMixin from '@/features/common/mixins/responsivenessMixin';
import eventBus from '@/features/common/eventBus';
import eventNames from '@/features/common/constants/events';

type FormData = GeneralInfo

@Component
export default class GeneralInfoComponent extends Mixins(ResponsivenessMixin) {
  @Inject({
    from: 'commercialInvoiceValidationState',
    default: () => ({ attempted: true }),
  })
  private commercialInvoiceValidationState!: CommercialInvoiceValidationState;

  @Ref('form') form!: VForm;

  private reasons: ReasonsForExportOptionsType = reasonsForExport;

  private currency: Array<keyof typeof CurrencyCodeType> = primaryCurrency;

  private declarationStatement = this.$t('shipment.declarationText') as string;

  private formData: FormData = {
    reasonForExport: '',
    generalDescriptionOfGoods: '',
    currencyCode: 'USD',
    remarks: '',
    declarationStatement: this.declarationStatement,
    taxId: '',
  };

  private requiredRules = requiredRules;

  private rulesIfAttempted(rules?: InputValidationRules): InputValidationRules {
    return rulesIfAttemptedFn(rules, this.commercialInvoiceValidationState.attempted);
  }

  private generalDescription: {
    items: string[],
    isLoading: boolean,
    search: string,
  } = {
    items: [],
    isLoading: false,
    search: '',
  };

  private isFormChanged = false;

  get isUserPreferences(): number {
    return this.$store.getters.getIsUserPreferences;
  }

  get userPreferences(): UserPreferences {
    return this.$store.getters.getUserPreferences;
  }

  get isInProgressData(): number {
    return this.$store.getters.getIsInProgressData;
  }

  get inProgressData(): InProgress {
    return this.$store.getters.getInProgress;
  }

  get toAddress(): ShipTo {
    return this.$store.getters.getShipToInProgress;
  }

  get fromAddress(): ShipFrom {
    return this.$store.getters.getShipFromInProgress;
  }

  get isShipmentToUS(): boolean {
    return this.toAddress.countryCode === 'US' && this.fromAddress.countryCode !== 'US';
  }

  @Watch('isUserPreferences', { immediate: true })
  private setUserPrefsData(newValue: number): void {
    if (!newValue) return;

    const { customsSettings } = this.userPreferences;
    if (!customsSettings) return;

    this.formData.reasonForExport = customsSettings.reasonForExport ?? '';
    this.formData.currencyCode = customsSettings.currencyCode || 'USD';
  }

  @Watch('isInProgressData', { immediate: true })
  private replaceData(newValue: number): void {
    if (!newValue) return;
    if (!this.inProgressData) return;

    const { toAddress } = this.inProgressData;
    this.formData.taxId = toAddress.taxId || '';

    if (!this.inProgressData.commercialInvoice) return;

    const {
      reasonForExport,
      generalDescriptionOfGoods,
      currencyCode,
      remarks,
      declarationStatement,
      taxId,
    } = this.inProgressData.commercialInvoice;

    const { customsSettings } = this.userPreferences;

    this.formData.reasonForExport = reasonForExport || customsSettings.reasonForExport;
    this.formData.currencyCode = currencyCode || customsSettings.currencyCode;
    this.formData.generalDescriptionOfGoods = generalDescriptionOfGoods;
    this.formData.remarks = remarks;
    this.formData.declarationStatement = declarationStatement || this.declarationStatement;
    this.formData.taxId = 'taxId' in this.inProgressData.commercialInvoice
      ? (taxId || '')
      : (toAddress.taxId || '');

    this.generalDescription.search = generalDescriptionOfGoods as string;
  }

  @Watch('generalDescription.search', { immediate: true })
  private async searchGeneralDescriptionItems(search: string) {
    if (!search) return;

    this.generalDescription.items.push(search);
    this.debounceSearchGoodsDescription(search);
  }

  mounted(): void {
    this.$store.commit('setGeneralInfoForm', this.$refs.form);
    eventBus.$on(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
  }

  destroyed(): void {
    eventBus.$off(eventNames.SHIPMENT_CLEAR_ALL_INPUTS, this.clearAllInformation);
  }

  private updateInProgressData(force = false) {
    if (!force && !this.isFormChanged) return;
    if (!this.inProgressData) return;

    const { commercialInvoice } = this.inProgressData;
    this.$store.dispatch('saveInProgressData', {
      key: 'commercialInvoice',
      value: {
        ...commercialInvoice,
        ...this.formData,
        customsParts: commercialInvoice
          ? commercialInvoice.customsParts || []
          : [],
      },
    });
  }

  private debounceSearchGoodsDescription = debounce(
    this.debounceSearchGoodsDescriptionHandler, 500,
  );

  private async debounceSearchGoodsDescriptionHandler(value: string) {
    this.generalDescription.isLoading = true;
    const items = await getCommercialInvoiceGeneralDescriptions(value);
    this.generalDescription.isLoading = false;
    if (!items) return;

    this.generalDescription.items = [
      value,
      ...items,
    ];
  }

  private onBlurGeneralGoodsDescription(event) {
    this.formData.generalDescriptionOfGoods = event.target.value;
    this.updateInProgressData(false);
  }

  private onTabPressedGeneralGoodsDescription(event) {
    this.onBlurGeneralGoodsDescription(event);

    const input = document.getElementById('ci-currency-code') as HTMLFormElement;
    if (!input) return;
    input.click();
  }

  private clearAllInformation() {
    (this.$refs.form as HTMLFormElement).resetValidation();
    this.isFormChanged = false;
  }
}
